/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ContentType } from "@dimensional-engineering/dimensional-models";
import { Roboto_Mono } from "next/font/google";
import html2canvas from "html2canvas";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";

import styles from "./WeeklyTwoByTwoModal.module.css";

import { RootState } from "@/redux/store";
import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import CloseButtonSummary from "@/components/shared/CloseButtonSummary";
import { useModalContext } from "@/context/ModalContext";
import { DailyStoriesInterractionBar } from "@/components/results/Snippets/SnippetInterractionBar/SnippetInterractionBar";
import { Mixpanel } from "@/helpers/mixpanel";
import { useAuth } from "@/context/AuthContext";
import ShareCaptionModal from "../ABSTRACTS/AppHomeSharedTab/ShareCaptionModal/ShareCaptionModal";
import { SharedPostWithCommentThread } from "@/models/sharedModels";
import SharedPostModal from "../ABSTRACTS/AppHomeSharedTab/SharedPostModal/SharedPostModal";
import LogoWithText from "@/components/shared/LogoWithText";
import {
  mapLlmPositionToPercent,
  PeerPoint,
  WeeklyTwoByTwoChart,
} from "./WeeklyTwoByTwoChart";
import { AnalysisFeedbackSnippets } from "@/components/results/personalityAnalysis/AnalysisFeedback/AnalysisFeedback";
import { db } from "@/_firebase/firebaseConfig";

const font = Roboto_Mono({ subsets: ["latin"] });

type ActiveTooltip = {
  caption: string;
  xPosition: number;
  yPosition: number;
  publicProfile: RootState["user"]["publicProfileData"];
  profileImage: string;
  sharedPost: SharedPostWithCommentThread;
};

export default function WeeklyTwoByTwoModal(props: {
  initialSelectedUserUid?: string;
}) {
  const { setModalOpen, setModalComponent } = useModalContext();
  const { user } = useAuth();

  const { latestChart, deliveredForLatest } = useSelector(
    (state: RootState) => state.weeklyTwoByTwo
  );
  const userPublicProfile = useSelector(
    (state: RootState) => state.user.publicProfileData
  );
  const userImageUrl = useSelector((state: RootState) => state.user.imageURL);
  const sharedPosts = useSelector((state: RootState) => state.sharedPosts).sharedPosts;
  const connectionProfiles = useSelector(
    (state: RootState) => state.connections.publicProfiles
  );

  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [caption, setCaption] = useState("");
  const [selectedFilterKey, setSelectedFilterKey] = useState<string>("all");
  const [activeTooltip, setActiveTooltip] = useState<ActiveTooltip | null>(null);
  const [showRating, setShowRating] = useState(false);
  const [feedbackScore, setFeedbackScore] = useState<number | null>(null);

  const foundPost = useIsWeeklyTwoByTwoShared(user?.uid, latestChart?.slug);
  const peerPoints = useWeeklyTwoByTwoSharedPeerPoints(
    latestChart?.slug,
    sharedPosts,
    connectionProfiles,
    user?.uid
  );

  const filterUsers = useMemo(() => {
    const users: Array<{ key: string; label: string }> = [];

    if (userPublicProfile?.userName) {
      users.push({
        key: "me",
        label: `@${userPublicProfile.userName}`,
      });
    }

    const seenConnectionKeys = new Set<string>();
    const sortedConnections = [...peerPoints]
      .sort((a, b) => a.userName.localeCompare(b.userName))
      .filter((point) => {
        const key = `connection:${point.authorUid}`;
        if (seenConnectionKeys.has(key)) {
          return false;
        }
        seenConnectionKeys.add(key);
        return true;
      });

    sortedConnections.forEach((point) => {
      users.push({
        key: `connection:${point.authorUid}`,
        label: `@${point.userName}`,
      });
    });

    return users;
  }, [peerPoints, userPublicProfile?.userName]);

  const pointsToRender = useMemo(() => {
    if (selectedFilterKey === "all") {
      return peerPoints;
    }

    if (selectedFilterKey === "me") {
      return [];
    }

    if (selectedFilterKey.startsWith("connection:")) {
      const selectedConnectionUid = selectedFilterKey.replace("connection:", "");
      return peerPoints.filter((point) => point.authorUid === selectedConnectionUid);
    }

    return peerPoints;
  }, [peerPoints, selectedFilterKey]);

  const showMyPoint = selectedFilterKey === "all" || selectedFilterKey === "me";

  const chartTitle = latestChart?.chartName?.toUpperCase() ?? "WEEKLY CHART";
  const safeImage = userImageUrl || "/default-image.png";

  useEffect(() => {
    if (!props.initialSelectedUserUid) {
      setSelectedFilterKey("all");
      return;
    }
    if (props.initialSelectedUserUid === user?.uid) {
      setSelectedFilterKey("me");
      return;
    }
    setSelectedFilterKey(`connection:${props.initialSelectedUserUid}`);
  }, [props.initialSelectedUserUid, user?.uid]);
  
  useEffect(() => {
    if (!latestChart?.slug || !deliveredForLatest) {
      return;
    }
    Mixpanel?.track(
      "Weekly Chart Viewed",
      {
        slug: latestChart.slug,
      },
      user?.uid
    );
  }, [deliveredForLatest, latestChart?.slug, user?.uid]);

  useEffect(() => {
    if (!user?.uid || !latestChart?.slug) {
      setFeedbackScore(null);
      return;
    }

    const deliveredRef = doc(db, "members", user.uid, "weeklyTwoByTwos", latestChart.slug);
    const unsubscribe = onSnapshot(deliveredRef, (snapshot) => {
      if (!snapshot.exists()) {
        setFeedbackScore(null);
        return;
      }
      const score = snapshot.data().feedbackScore;
      if (score === null || score === undefined) {
        setFeedbackScore(null);
        return;
      }
      setFeedbackScore(score as number);
      setShowRating(false);
    });

    return () => {
      unsubscribe();
    };
  }, [latestChart?.slug, user?.uid]);

  if (!latestChart || !deliveredForLatest) {
    return null;
  }

  function openSharedPostFromTooltip() {
    if (!activeTooltip) {
      return;
    }
    setModalComponent(
      <SharedPostModal
        profileImage={activeTooltip.profileImage}
        publicProfile={activeTooltip.publicProfile}
        sharedPost={activeTooltip.sharedPost}
      />
    );
  }

  function handleMyPointClick() {
    if (!foundPost) {
      return;
    }
    setActiveTooltip({
      caption: foundPost.caption ?? "",
      xPosition: deliveredForLatest!.xPosition,
      yPosition: deliveredForLatest!.yPosition,
      publicProfile: userPublicProfile,
      profileImage: safeImage,
      sharedPost: foundPost,
    });
  }

  function handlePeerPointClick(point: PeerPoint) {
    const peerPost = sharedPosts.find((post) => {
      return (
        post.postContent.contentIdentifier.contentType === ContentType.weeklyTwoByTwo &&
        post.postContent.contentIdentifier.contentSlug === latestChart!.slug &&
        post.authorUid === point.authorUid
      );
    });

    if (!peerPost) {
      return;
    }

    const trimmedCaption = (peerPost.caption ?? "").trim();
    if (!trimmedCaption) {
      return;
    }

    setActiveTooltip({
      caption: trimmedCaption,
      xPosition: point.xPosition,
      yPosition: point.yPosition,
      publicProfile: point.publicProfile,
      profileImage: point.imageUrl,
      sharedPost: peerPost as SharedPostWithCommentThread,
    });
  }

  function handlePost() {
    if (foundPost) {
      Mixpanel?.track(
        "Post viewed",
        {
          source: "from own results modal",
          content_type: "weekly chart",
          content_type_slug: latestChart?.slug,
          caption_text: foundPost.caption,
        },
        user?.uid
      );
      return setModalComponent(
        <SharedPostModal
          profileImage={safeImage}
          publicProfile={userPublicProfile}
          sharedPost={foundPost}
        />
      );
    }

    return setShowCaptionModal(true);
  }

  async function handleRating(value: number) {
    if (!user?.uid || !latestChart?.slug) {
      return;
    }

    await updateDoc(doc(db, "members", user.uid, "weeklyTwoByTwos", latestChart.slug), {
      feedbackScore: value,
    });

    setFeedbackScore(value);
    setShowRating(false);

    Mixpanel?.track(
      "Feedback given",
      {
        slug: latestChart.slug,
        score: value,
        content_type: "weekly_two_by_two",
      },
      user?.uid
    );
  }

  return (
    <>
      {showCaptionModal && (
        <ShareCaptionModal
          contentIdentifier={{
            contentSlug: latestChart.slug,
            contentType: ContentType.weeklyTwoByTwo,
          }}
          onClose={(captionText) => {
            setCaption(captionText);
            setShowCaptionModal(false);
          }}
          savedCaption={caption}
        />
      )}
      <ModalWrapper title="" noHeader noLine>
        <main className={styles.main} onClick={() => setActiveTooltip(null)}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => setModalOpen(false)}
          >
            <CloseButtonSummary />
          </button>

          <h2 className={`${styles.chartTitle} ${font.className}`}>{chartTitle}</h2>

          <div className={styles.chartFrame}>
            <WeeklyTwoByTwoChart
              topAxisLabel={latestChart.topAxisLabel}
              bottomAxisLabel={latestChart.bottomAxisLabel}
              leftAxisLabel={latestChart.leftAxisLabel}
              rightAxisLabel={latestChart.rightAxisLabel}
              myPoint={deliveredForLatest}
              myImageUrl={safeImage}
              myPublicProfile={userPublicProfile}
              peerPoints={pointsToRender}
              showMyPoint={showMyPoint}
              fullWidth
              paddedInner
              onMyPointClick={handleMyPointClick}
              onPeerPointClick={handlePeerPointClick}
            />
            {activeTooltip && (
              <button
                type="button"
                className={styles.captionTooltip}
                style={{
                  left: `${mapLlmPositionToPercent(activeTooltip.xPosition)}%`,
                  bottom: `calc(${mapLlmPositionToPercent(activeTooltip.yPosition)}% + 30px)`,
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  openSharedPostFromTooltip();
                }}
              >
                {activeTooltip.caption || "View shared post"}
              </button>
            )}
          </div>

          <div className={styles.filterRow}>
            <span className={styles.showLabel}>Show:</span>
            <button
              type="button"
              className={selectedFilterKey === "all" ? styles.filterActive : styles.filter}
              onClick={() => setSelectedFilterKey("all")}
            >
              All
            </button>
            {filterUsers.map((filterUser) => (
              <button
                type="button"
                key={filterUser.key}
                className={
                  selectedFilterKey === filterUser.key
                    ? styles.filterActive
                    : styles.filter
                }
                onClick={() => setSelectedFilterKey(filterUser.key)}
              >
                {filterUser.label}
              </button>
            ))}
          </div>

          <div className={styles.interactionBar}>
            {showRating && (
              <div className={styles.feedbackDiv}>
                <AnalysisFeedbackSnippets
                  onAnswer={(value) => {
                    handleRating(value);
                  }}
                  title="How good was this content?"
                  subtitle="Feedback improves your results"
                  currentAnswer={feedbackScore}
                  onClose={() => setShowRating(false)}
                />
              </div>
            )}
            <DailyStoriesInterractionBar
              onRateClick={() => {
                setActiveTooltip(null);
                setShowRating(!showRating);
              }}
              onCommentClick={() => {}}
              onGoDeeper={() => {}}
              onPostClick={() => {
                Mixpanel?.track(
                  "Post to connections CTA pressed",
                  {
                    content_type: "weekly chart",
                    slug: latestChart.slug,
                  },
                  user?.uid
                );
                handlePost();
              }}
              posted={!!foundPost}
              commentCount={0}
              onShare={(type) => {
                Mixpanel?.track(
                  "Shared to Social",
                  {
                    platform: type === "instagram" ? "Instagram" : "Snapchat",
                    type: "weeklyTwoByTwo",
                    slug: latestChart.slug,
                  },
                  user?.uid
                );
                setModalComponent(<WeeklyTwoByTwoShareView type={type} />);
              }}
            />
          </div>
        </main>
      </ModalWrapper>
    </>
  );
}

export function WeeklyTwoByTwoShareView(props: { type: "instagram" | "snapchat" }) {
  const { setModalOpen } = useModalContext();
  const imageRef = useRef<HTMLDivElement>(null);

  const { latestChart, deliveredForLatest } = useSelector(
    (state: RootState) => state.weeklyTwoByTwo
  );
  const userPublicProfile = useSelector(
    (state: RootState) => state.user.publicProfileData
  );
  const userImageUrl = useSelector((state: RootState) => state.user.imageURL);

  useEffect(() => {
    const handleDownloadImage = async () => {
      if (!imageRef.current) {
        return;
      }
      const canvas = await html2canvas(imageRef.current);
      const data = canvas.toDataURL("image/jpg");

      if (window.Android) {
        window.Android.onHaptic();
        if (props.type === "instagram") {
          window.Android.shareToInstagramQuiz(data);
        } else {
          window.Android.shareToSnapChat(data);
        }
      }
    };

    setTimeout(() => {
      handleDownloadImage();
      setModalOpen(false);
    }, 110);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!latestChart || !deliveredForLatest) {
    return null;
  }

  return (
    <ModalWrapper title="" noHeader noLine>
      <main ref={imageRef} className={styles.shareMain}>
        <div className={styles.shareLogo}>
          <LogoWithText />
        </div>
        <h2 className={`${styles.chartTitle} ${font.className}`}>
          {latestChart.chartName.toUpperCase()}
        </h2>
        <div className={styles.chartFrame}>
          <WeeklyTwoByTwoChart
            topAxisLabel={latestChart.topAxisLabel}
            bottomAxisLabel={latestChart.bottomAxisLabel}
            leftAxisLabel={latestChart.leftAxisLabel}
            rightAxisLabel={latestChart.rightAxisLabel}
            myPoint={deliveredForLatest}
            myImageUrl={userImageUrl || "/default-image.png"}
            myPublicProfile={userPublicProfile}
            peerPoints={[]}
            fullWidth
            paddedInner
          />
        </div>
      </main>
    </ModalWrapper>
  );
}

function useIsWeeklyTwoByTwoShared(
  userUid: string | undefined,
  chartSlug: string | undefined
) {
  const sharedPosts = useSelector(
    (state: RootState) => state.sharedPosts
  ).sharedPosts;

  const [foundPost, setFoundPost] =
    useState<SharedPostWithCommentThread | null>(null);

  useEffect(() => {
    const slug = `${userUid}-weeklyTwoByTwo-${chartSlug}`;
    const isFound = sharedPosts.find((post) => post.slug === slug);
    setFoundPost((isFound as SharedPostWithCommentThread) ?? null);
  }, [chartSlug, sharedPosts, userUid]);

  return foundPost;
}

function useWeeklyTwoByTwoSharedPeerPoints(
  chartSlug: string | undefined,
  sharedPosts: RootState["sharedPosts"]["sharedPosts"],
  connectionProfiles: RootState["connections"]["publicProfiles"],
  userUid: string | undefined
) {
  return useMemo(() => {
    if (!chartSlug) {
      return [];
    }

    const postsForChart = sharedPosts.filter((post) => {
      return (
        post.postContent.contentIdentifier.contentType ===
          ContentType.weeklyTwoByTwo &&
        post.postContent.contentIdentifier.contentSlug === chartSlug &&
        post.authorUid !== userUid &&
        post.postContent.weeklyTwoByTwoData?.deliveredWeeklyTwoByTwo
      );
    });

    return postsForChart
      .map((post) => {
        const connection = connectionProfiles.find(
          (profile) => profile.ownerUUID === post.authorUid
        );
        const delivered = post.postContent.weeklyTwoByTwoData?.deliveredWeeklyTwoByTwo;

        if (!connection || !delivered) {
          return null;
        }

        return {
          authorUid: post.authorUid,
          xPosition: delivered.xPosition,
          yPosition: delivered.yPosition,
          imageUrl: connection.imageUrl,
          userName: connection.userName,
          publicProfile: connection,
        } as PeerPoint;
      })
      .filter((point): point is PeerPoint => !!point);
  }, [chartSlug, connectionProfiles, sharedPosts, userUid]);
}
